import { useState, useCallback, useMemo, useEffect } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy, Check, ArrowRightLeft, Search, Ruler, Weight, Thermometer,
  Square, Box, Gauge, Clock, HardDrive, Zap
} from 'lucide-react'

interface UnitCategory {
  key: string
  icon: React.ReactNode
  units: { key: string; name: string; factor: number }[]
}

const CATEGORIES: UnitCategory[] = [
  {
    key: 'length',
    icon: <Ruler className="w-4 h-4" />,
    units: [
      { key: 'm', name: 'Meter', factor: 1 },
      { key: 'km', name: 'Kilometer', factor: 1000 },
      { key: 'cm', name: 'Centimeter', factor: 0.01 },
      { key: 'mm', name: 'Millimeter', factor: 0.001 },
      { key: 'mi', name: 'Mile', factor: 1609.344 },
      { key: 'yd', name: 'Yard', factor: 0.9144 },
      { key: 'ft', name: 'Foot', factor: 0.3048 },
      { key: 'in', name: 'Inch', factor: 0.0254 },
    ]
  },
  {
    key: 'weight',
    icon: <Weight className="w-4 h-4" />,
    units: [
      { key: 'kg', name: 'Kilogram', factor: 1 },
      { key: 'g', name: 'Gram', factor: 0.001 },
      { key: 'mg', name: 'Milligram', factor: 1e-6 },
      { key: 't', name: 'Metric Ton', factor: 1000 },
      { key: 'lb', name: 'Pound', factor: 0.453592 },
      { key: 'oz', name: 'Ounce', factor: 0.0283495 },
    ]
  },
  {
    key: 'temperature',
    icon: <Thermometer className="w-4 h-4" />,
    units: [
      { key: 'c', name: 'Celsius', factor: 1 },
      { key: 'f', name: 'Fahrenheit', factor: 1 },
      { key: 'k', name: 'Kelvin', factor: 1 },
    ]
  },
  {
    key: 'area',
    icon: <Square className="w-4 h-4" />,
    units: [
      { key: 'm2', name: 'Square Meter', factor: 1 },
      { key: 'km2', name: 'Square Kilometer', factor: 1e6 },
      { key: 'ha', name: 'Hectare', factor: 10000 },
      { key: 'ac', name: 'Acre', factor: 4046.86 },
      { key: 'ft2', name: 'Square Foot', factor: 0.092903 },
    ]
  },
  {
    key: 'volume',
    icon: <Box className="w-4 h-4" />,
    units: [
      { key: 'l', name: 'Liter', factor: 1 },
      { key: 'ml', name: 'Milliliter', factor: 0.001 },
      { key: 'm3', name: 'Cubic Meter', factor: 1000 },
      { key: 'gal', name: 'Gallon (US)', factor: 3.78541 },
      { key: 'qt', name: 'Quart (US)', factor: 0.946353 },
      { key: 'cup', name: 'Cup (US)', factor: 0.236588 },
    ]
  },
  {
    key: 'speed',
    icon: <Gauge className="w-4 h-4" />,
    units: [
      { key: 'm/s', name: 'Meter/Second', factor: 1 },
      { key: 'km/h', name: 'Kilometer/Hour', factor: 0.277778 },
      { key: 'mph', name: 'Mile/Hour', factor: 0.44704 },
      { key: 'kn', name: 'Knot', factor: 0.514444 },
    ]
  },
  {
    key: 'time',
    icon: <Clock className="w-4 h-4" />,
    units: [
      { key: 's', name: 'Second', factor: 1 },
      { key: 'min', name: 'Minute', factor: 60 },
      { key: 'h', name: 'Hour', factor: 3600 },
      { key: 'd', name: 'Day', factor: 86400 },
      { key: 'wk', name: 'Week', factor: 604800 },
      { key: 'mo', name: 'Month (avg)', factor: 2.628e6 },
      { key: 'y', name: 'Year', factor: 3.154e7 },
    ]
  },
  {
    key: 'data',
    icon: <HardDrive className="w-4 h-4" />,
    units: [
      { key: 'b', name: 'Byte', factor: 1 },
      { key: 'kb', name: 'Kilobyte', factor: 1024 },
      { key: 'mb', name: 'Megabyte', factor: 1048576 },
      { key: 'gb', name: 'Gigabyte', factor: 1073741824 },
      { key: 'tb', name: 'Terabyte', factor: 1099511627776 },
      { key: 'bit', name: 'Bit', factor: 0.125 },
    ]
  },
  {
    key: 'energy',
    icon: <Zap className="w-4 h-4" />,
    units: [
      { key: 'j', name: 'Joule', factor: 1 },
      { key: 'kj', name: 'Kilojoule', factor: 1000 },
      { key: 'cal', name: 'Calorie', factor: 4.184 },
      { key: 'kcal', name: 'Kilocalorie', factor: 4184 },
      { key: 'wh', name: 'Watt-Hour', factor: 3600 },
      { key: 'kwh', name: 'Kilowatt-Hour', factor: 3.6e6 },
    ]
  },
]

function convertTemperature(value: number, from: string, to: string): number {
  let celsius: number
  switch (from) {
    case 'c': celsius = value; break
    case 'f': celsius = (value - 32) * 5/9; break
    case 'k': celsius = value - 273.15; break
    default: celsius = value
  }
  switch (to) {
    case 'c': return celsius
    case 'f': return celsius * 9/5 + 32
    case 'k': return celsius + 273.15
    default: return celsius
  }
}

function convert(value: number, fromFactor: number, toFactor: number, isTemp: boolean, fromKey: string, toKey: string): number {
  if (isTemp) return convertTemperature(value, fromKey, toKey)
  return value * fromFactor / toFactor
}

function formatResult(value: number): string {
  if (value === 0) return '0'
  if (Math.abs(value) < 0.000001 || Math.abs(value) > 1e12) return value.toExponential(6)
  return parseFloat(value.toPrecision(10)).toString()
}

function App() {
  const { t } = useI18n()
  const [selectedCategory, setSelectedCategory] = useState(0)
  const [fromUnit, setFromUnit] = useState(0)
  const [toUnit, setToUnit] = useState(1)
  const [inputValue, setInputValue] = useState('1')
  const [copied, setCopied] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const category = CATEGORIES[selectedCategory]
  const isTemp = category.key === 'temperature'

  useEffect(() => {
    setFromUnit(0)
    setToUnit(Math.min(1, category.units.length - 1))
    setInputValue('1')
  }, [selectedCategory])

  const fromUnitData = category.units[fromUnit]
  const toUnitData = category.units[toUnit]

  const result = useMemo(() => {
    const val = parseFloat(inputValue)
    if (isNaN(val)) return ''
    const converted = convert(val, fromUnitData.factor, toUnitData.factor, isTemp, fromUnitData.key, toUnitData.key)
    return formatResult(converted)
  }, [inputValue, fromUnitData, toUnitData, isTemp])

  const swapUnits = useCallback(() => {
    setFromUnit(toUnit)
    setToUnit(fromUnit)
  }, [fromUnit, toUnit])

  const copyResult = useCallback(async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [result])

  const filteredCategories = CATEGORIES.filter(cat => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return cat.key.toLowerCase().includes(q) ||
      cat.units.some(u => u.name.toLowerCase().includes(q) || u.key.toLowerCase().includes(q))
  })

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('unitConverter.title') || 'Unit Converter'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('unitConverter.subtitle') || 'Convert between 10+ unit categories. '}
          <span className="text-primary font-medium">{t('unitConverter.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('unitConverter.searchPlaceholder') || 'Search categories or units...'}
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-brand-primary/50"
          />
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2">
          {filteredCategories.map((cat) => {
            const actualIdx = CATEGORIES.indexOf(cat)
            return (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(actualIdx)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === actualIdx
                    ? 'bg-brand-primary text-white'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                {cat.icon}
                {t(`unitConverter.${cat.key}`) || cat.key}
              </button>
            )
          })}
        </div>

        {/* Converter Panel */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-6">
          {/* From */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t('unitConverter.from') || 'From'}</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="number"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground text-lg font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
                placeholder="1"
              />
              <select
                value={fromUnit}
                onChange={e => setFromUnit(Number(e.target.value))}
                className="sm:w-48 px-3 py-3 bg-background border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-brand-primary/50"
              >
                {category.units.map((u, i) => (
                  <option key={u.key} value={i}>{t(`unitConverter.${u.key}`) || u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Swap */}
          <div className="flex justify-center">
            <button
              onClick={swapUnits}
              className="p-2.5 bg-muted rounded-full hover:bg-muted/80 transition-colors"
            >
              <ArrowRightLeft className="w-5 h-5 text-brand-primary" />
            </button>
          </div>

          {/* To */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t('unitConverter.to') || 'To'}</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground text-lg font-mono min-h-[54px] flex items-center">
                {result || '—'}
              </div>
              <select
                value={toUnit}
                onChange={e => setToUnit(Number(e.target.value))}
                className="sm:w-48 px-3 py-3 bg-background border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-brand-primary/50"
              >
                {category.units.map((u, i) => (
                  <option key={u.key} value={i}>{t(`unitConverter.${u.key}`) || u.name}</option>
                ))}
              </select>
            </div>
            {result && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {inputValue} {t(`unitConverter.${fromUnitData.key}`) || fromUnitData.name} = {result} {t(`unitConverter.${toUnitData.key}`) || toUnitData.name}
                </span>
                <button
                  onClick={copyResult}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Conversions */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            {t('unitConverter.quickConversions') || 'Quick Conversions'}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {category.units.slice(0, 8).map((u, i) => {
              if (i === fromUnit) return null
              const val = parseFloat(inputValue)
              if (isNaN(val)) return null
              const conv = convert(val, fromUnitData.factor, u.factor, isTemp, fromUnitData.key, u.key)
              return (
                <button
                  key={u.key}
                  onClick={() => { setToUnit(i); }}
                  className="text-left px-3 py-2 bg-background border border-border rounded-lg hover:border-brand-primary transition-colors"
                >
                  <div className="text-xs text-muted-foreground">{t(`unitConverter.${u.key}`) || u.name}</div>
                  <div className="text-sm font-mono text-foreground truncate">{formatResult(conv)}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{t('security.note')}</p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
